/**
 * ResolveScheduler - A utility class that processes queued items in controlled batches at timed intervals.
 * This is useful for rate-limited API calls, processing large datasets, or any task that benefits from
 * controlled, throttled execution.
 */
export class ResolveScheduler<T> {
  /** Maximum number of items to process per interval */
  private _batchSize: number;
  /** Time between processing batches in milliseconds */
  private _interval: number;
  /** Reference to the active interval timer or null when inactive */
  private _intervalHandle: NodeJS.Timeout | null = null;
  /** Queue of items waiting to be processed */
  private _queueToResolve: T[] = [];
  /** Function that will be called for each processed item */
  private _resolver: (object: T) => void;

  /**
   * Creates a new ResolveScheduler
   * @param args Configuration object
   * @param args.batchSize Maximum number of items to process in each interval
   * @param args.interval Time in milliseconds between processing batches
   * @param args.resolver Function that will be called for each processed item
   */
  constructor(args: { batchSize: number; interval: number; resolver: (object: T) => void }) {
    this._batchSize = args.batchSize;
    this._interval = args.interval;
    this._resolver = args.resolver;
  }

  /**
   * Adds an item to the processing queue
   * @param object The item to be processed
   */
  enqueue = (object: T) => {
    this._queueToResolve.push(object);
  };

  /**
   * Starts the processing interval if not already running.
   * The scheduler will automatically stop when the queue is empty.
   */
  startScheduler = () => {
    if (this._intervalHandle !== null) return;

    this._intervalHandle = setInterval(() => {
      // Process up to batchSize items in each interval
      for (let i = 0; i < this._batchSize; i++) {
        const next = this._queueToResolve.shift();
        if (!next) break;
        this._resolver(next);
      }

      // Auto-stop when queue is empty
      if (this._queueToResolve.length === 0) {
        if (this._intervalHandle !== null) clearInterval(this._intervalHandle);
        this._intervalHandle = null;
      }
    }, this._interval) as NodeJS.Timeout;
  };

  resolve = (object: T) => {
    this.enqueue(object);
    this.startScheduler();
  };
}
